import { useMemo, useState } from 'react';
import {
  PLACEHOLDER_BOUNDS,
  POSITION_SOURCE_LABEL,
  checkPosition,
  formatCoord,
  parseCoordinates,
  propertyById,
  type PositionSource,
} from '@kilroy/shared';

export interface ManualFix {
  lat: number;
  lon: number;
  accuracy: number;
  source: PositionSource;
}

/* Typing a position off a handheld GPS.
 *
 * The team carries dedicated units, so this is not a fallback for when the
 * phone fails — it is frequently the better number, and often the only one
 * available with the phone's radio off in a canyon.
 *
 * Three things this screen has to get right:
 *
 *   · Accept what the unit prints. Decimal, degrees-minutes, DMS and UTM all
 *     parse. Making a geologist convert UTM in their head at an outcrop is how
 *     a digit gets transposed.
 *   · Take the handheld's own accuracy. It knows its EPE and the phone does
 *     not; typing "±3" from a Garmin is a truer number than anything we could
 *     infer, and pretending otherwise would throw away the reason to use it.
 *   · Warn, never block. A transposed digit puts a record a thousand kilometres
 *     away and looks entirely normal on screen — but a geologist may also
 *     legitimately record float in a wash two valleys over, and refusing that
 *     save is the one unforgivable behaviour in this app.
 */

const SOURCES: PositionSource[] = ['handheld_gps', 'manual_entry', 'map_estimate'];

export function ManualPosition({
  propertyId,
  onApply,
  onCancel,
}: {
  propertyId: string;
  onApply: (fix: ManualFix) => void;
  onCancel: () => void;
}) {
  const [text, setText] = useState('');
  const [accuracy, setAccuracy] = useState('');
  const [source, setSource] = useState<PositionSource>('handheld_gps');

  const parsed = useMemo(() => parseCoordinates(text), [text]);
  const property = propertyById(propertyId);

  const check = useMemo(
    () =>
      parsed
        ? checkPosition(parsed.lat, parsed.lon, property, { placeholder: PLACEHOLDER_BOUNDS })
        : null,
    [parsed, property],
  );

  return (
    <div className="mpos">
      <div className="field">
        <label className="label" htmlFor="mpos-c">
          Coordinates
        </label>
        <input
          id="mpos-c"
          className="input"
          value={text}
          onChange={(ev) => setText(ev.target.value)}
          placeholder="34.53120, -112.41080"
          autoComplete="off"
          autoCapitalize="off"
          spellCheck={false}
          inputMode="text"
        />
        {/* The formats, shown rather than described. Someone holding a unit set
            to UTM should be able to see at a glance that it will be accepted. */}
        <p className="field__hint mpos__formats">
          decimal · 34 31.872 N 112 24.648 W · 34°31'52"N · 12S 370528 3821961
        </p>
      </div>

      {/* What was understood, echoed back. A parser that silently reinterprets
          input is worse than one that refuses it. */}
      {parsed && (
        <div className="mpos__read">
          <span className="mono mpos__coords">
            {formatCoord(parsed.lat, 'lat')} · {formatCoord(parsed.lon, 'lon')}
          </span>
          <span className="mono mpos__fmt">read as {parsed.format}</span>
        </div>
      )}

      {check?.message && (
        <p className={`mpos__warn mpos__warn--${check.verdict}`}>{check.message}</p>
      )}

      <div className="mpos__row">
        <div className="field grow">
          <label className="label" htmlFor="mpos-a">
            Accuracy the unit reports
          </label>
          <div className="mpos__acc">
            <span className="mono">±</span>
            <input
              id="mpos-a"
              className="input"
              value={accuracy}
              onChange={(ev) => setAccuracy(ev.target.value.replace(/[^\d.]/g, ''))}
              placeholder="3"
              inputMode="decimal"
            />
            <span className="mono">m</span>
          </div>
        </div>
      </div>

      <div className="field">
        <div className="label">Where it came from</div>
        <div className="mpos__srcs">
          {SOURCES.map((s) => (
            <button
              key={s}
              type="button"
              className="chip"
              aria-pressed={source === s}
              onClick={() => setSource(s)}
            >
              {POSITION_SOURCE_LABEL[s]}
            </button>
          ))}
        </div>
      </div>

      <div className="mpos__actions">
        <button
          type="button"
          className="btn btn--primary btn--lg"
          disabled={!parsed}
          onClick={() =>
            parsed &&
            onApply({
              lat: parsed.lat,
              lon: parsed.lon,
              // -1 means "unknown", which is honest. A blank box is not a
              // claim of perfect accuracy and must not be recorded as one.
              accuracy: accuracy.trim() === '' ? -1 : Number(accuracy),
              source,
            })
          }
        >
          Use this position
        </button>
        <button type="button" className="btn btn--quiet btn--lg" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </div>
  );
}
